/**
 * Cross-environment lazy module loader.
 *
 * Uses the environment's `require` function (if available) to load
 * optional peer dependencies at runtime. Returns null when:
 * - The environment doesn't support `require` (e.g. browser without bundler)
 * - The module is not installed
 *
 * This avoids importing `@types/node` in browser-only tsconfigs while
 * still supporting lazy-loading in Node environments and bundlers.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Attempt to require a module by name. Returns null if unavailable.
 */
export function lazyRequire<T = any>(moduleName: string): T | null {
  try {
    // 1. Some bundlers expose `require` on globalThis.
    let _require = (globalThis as Record<string, any>)['require'] as
      | ((id: string) => any)
      | undefined;

    // 2. In CommonJS (Node, tsc/esbuild output) `require` is a module-local
    //    binding, NOT on globalThis — so step 1 misses it. Reach it via an
    //    indirect eval that yields `undefined` (instead of a ReferenceError)
    //    in ESM/browser builds where `require` does not exist.
    if (typeof _require !== 'function') {
      try {
        _require = eval('typeof require === "function" ? require : undefined');
      } catch {
        _require = undefined;
      }
    }

    if (typeof _require === 'function') {
      return _require(moduleName) as T;
    }

    return null;
  } catch {
    return null;
  }
}
